// Thin fetch wrapper. Reads/writes go through workers-api with session cookie.

import type { PortfolioPanelResponses, PortfolioQueryParams } from "@finance-os/shared-types/api";
import type { PortfolioPanelConst } from "@finance-os/shared-types/constants";
// The REST update body, declared once beside the route's schema (secrets: null clears, an object is encrypted).
import type { IntegrationUpdate } from "@finance-os/shared-types/zod";
import { portfolioQueryString } from "./lib/portfolio-query.js";

// Local dev: vite proxies /api/* → workers-api on localhost, injecting the
// CF Access email header. Production: hit finance-api.kostanos.com directly,
// CF Access at the edge mints the JWT. `VITE_API_URL` overrides both.
const API_BASE =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.DEV ? "/api" : "https://finance-api.kostanos.com");

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  // `no-store` keeps every read off the browser HTTP cache. List responses carry
  // no Cache-Control/ETag, so without this a GET can be served stale — the new row
  // wouldn't show up after a create OR a full page reload (the list-freshness
  // contract on the Accounts Management feature). Callers may override via init.cache.
  const res = await fetch(`${API_BASE}${path}`, { credentials: "include", cache: "no-store", ...init, headers });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${String(res.status)}: ${text}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const json = <T,>(method: "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> => {
  const init: RequestInit = { method };
  if (body !== undefined) init.body = JSON.stringify(body);
  return req<T>(path, init);
};

export const api = {
  // Auth
  me: () => req<{ user: { id: string; email: string } | null }>("/auth/me"),
  loginUrl: `${API_BASE}/auth/login`,
  logout: () => req<{ ok: true }>("/auth/logout", { method: "POST" }),
  wsToken: () => req<{ token: string; exp: number }>("/auth/ws-token"),

  // Generic CRUD helpers — all routes share the shape
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- typed-fetch ergonomic: caller names the row type, e.g. api.list<Account>("/accounts")
  list: <T>(path: string) => req<{ items: T[] }>(path),
  get: <T>(path: string) => req<T>(path),
  create: <T>(path: string, body: unknown) => json<T>("POST", path, body),
  update: <T>(path: string, body: unknown) => json<T>("PATCH", path, body),
  remove: (path: string) => json<{ ok: true; deleted_id: string }>("DELETE", path),

  // Analytics
  netWorth: () => req<NetWorth>("/analytics/net-worth"),
  fireScores: () => req<{ as_of: string; scores: FireScore[] }>("/analytics/fire-scores"),
  spending: (period: string) => req<SpendingSummary>(`/analytics/spending?period=${period}`),
  emergencyFund: () => req<{ as_of: string; liquid_eur_minor: number; monthly_burn_eur_minor: number; months_runway: number; has_spend_data: boolean }>("/analytics/emergency-fund"),
  cashFlow: () => req<CashFlow>("/analytics/cash-flow"),
  retirementProjections: (targetYear?: number) =>
    req<RetirementProjections>(
      `/analytics/retirement-projections${targetYear ? `?target_year=${String(targetYear)}` : ""}`,
    ),

  // Portfolio dashboard (FT-70): one endpoint per panel, all taking the same query.
  portfolio: <P extends PortfolioPanelConst>(panel: P, params: PortfolioQueryParams) =>
    req<PortfolioPanelResponses[P]>(`/analytics/portfolio/${panel}${portfolioQueryString(params)}`),

  // Stocks portfolio (FT-69)
  holdings: () => req<HoldingsResponse>("/stocks/holdings"),
  buyingPower: () => req<{ as_of: string; items: BuyingPowerRow[] }>("/stocks/buying-power"),
  lots: () => req<{ as_of: string; items: Lot[] }>("/stocks/lots"),

  // Securities master (FT-73/74/75 support)
  securities: (params: { q?: string; asset_class?: string; limit?: number } = {}) => {
    const s = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== "") s.set(k, String(v));
    return req<{ items: Security[]; pagination: Pagination }>(`/securities?${s.toString()}`);
  },

  // Daily prices (FT-74)
  securityPrices: (params: { security_id?: string; source?: string; from?: string; to?: string; limit?: number; offset?: number } = {}) => {
    const s = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== "") s.set(k, String(v));
    return req<{ items: SecurityPrice[]; pagination: Pagination }>(`/security-prices?${s.toString()}`);
  },
  collectPrices: () => json<{ written: number }>("POST", "/security-prices/collect"),

  // News (FT-75)
  securityNews: (params: { q?: string; source?: string; limit?: number; offset?: number } = {}) => {
    const s = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== "") s.set(k, String(v));
    return req<{ items: SecurityNews[]; pagination: Pagination }>(`/security-news?${s.toString()}`);
  },
  ingestNews: (securityId?: string) =>
    json<{ securities: number; articles: number; failed: number }>("POST", `/security-news/ingest${securityId ? `?security_id=${securityId}` : ""}`),
  stockNews: (id: string) => req<{ items: StockNewsItem[] }>(`/stocks/${id}/news`),

  // Per-security analytics (FT-73)
  stockPerformance: () => req<{ as_of: string; items: StockPerformanceRow[] }>("/stocks/performance"),
  stockDetail: (id: string) => req<{ security: Security; performance: SecurityPerformanceRow | null }>(`/stocks/${id}/detail`),
  stockLots: (id: string) => req<{ items: SecurityLot[] }>(`/stocks/${id}/lots`),
  stockDividends: (id: string) => req<{ items: Transaction[] }>(`/stocks/${id}/dividends`),
  stockTxns: (id: string) => req<{ items: Transaction[] }>(`/stocks/${id}/transactions`),
  stockPrices: (id: string) => req<{ items: SecurityPrice[] }>(`/stocks/${id}/prices`),
  stockPerformanceHistory: (id: string) => req<{ items: SecurityPerformanceRow[] }>(`/stocks/${id}/performance-history`),
  recomputeStocks: () => json<{ securities: number; rows: number; failed: number }>("POST", "/stocks/recompute"),

  // Admin sync triggers
  syncFx: () => json<{ ok: true; inserted: number; date: string }>("POST", "/admin/sync/fx"),
  syncBanks: () => json<{ ok: true; providers: { provider: string; status: string }[] }>("POST", "/admin/sync/banks"),

  // Settings catalog — generic key/value with per-type editors
  settings: (params: { tag?: string; q?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.tag) qs.set("tag", params.tag);
    if (params.q) qs.set("q", params.q);
    const suffix = qs.toString();
    return req<{ items: SettingPublic[] }>(`/settings${suffix ? "?" + suffix : ""}`);
  },
  setting: (code: string) => req<SettingPublic>(`/settings/${code}`),
  settingUpdate: (code: string, body: { value?: unknown; enabled?: boolean }) =>
    json<SettingPublic>("PUT", `/settings/${code}`, body),
  settingClear: (code: string) => json<SettingPublic>("DELETE", `/settings/${code}`),

  // Integrations — generic CRUD + dispatch (keyed by row id, not code,
  // so multi-cardinality codes like "gdrive" can have many rows).
  integrations: () => req<{ items: IntegrationPublic[] }>("/integrations"),
  integration: (id: string) => req<IntegrationPublic>(`/integrations/${id}`),
  integrationCreate: (body: { code: string; label?: string }) => json<IntegrationPublic>("POST", `/integrations`, body),
  integrationUpdate: (id: string, body: IntegrationUpdate) => json<IntegrationPublic>("PUT", `/integrations/${id}`, body),
  integrationDelete: (id: string) => json<{ ok: true; deleted_id: string }>("DELETE", `/integrations/${id}`),
  integrationTest: (id: string) => json<{ ok: boolean; detail?: string }>("POST", `/integrations/${id}/test`),
  integrationSync: (id: string) => json<{ ok: boolean; updated?: number; note?: string; error?: string }>("POST", `/integrations/${id}/sync`),
  integrationLog: (id: string, limit = 5) => req<{ items: IntegrationLogEntry[] }>(`/integrations/${id}/log?limit=${String(limit)}`),
  // Cross-integration log feed — fully query-param driven (URL = state).
  integrationLogs: (params: { integration?: string | undefined; status?: string | undefined; kind?: string | undefined; search?: string | undefined; limit?: number | undefined; offset?: number | undefined } = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") q.set(k, String(v));
    return req<{ items: IntegrationLogRow[]; pagination: { total: number; limit: number; offset: number } }>(
      `/integrations/logs?${q.toString()}`,
    );
  },

  // Transactions has filters + pagination
  transactions: (params: Record<string, string | number | undefined> = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined) q.set(k, String(v));
    return req<{ items: Transaction[]; pagination: { total: number; limit: number; offset: number } }>(
      `/transactions?${q.toString()}`,
    );
  },

  // Documents upload
  uploadFile: async (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API_BASE}/uploads`, { method: "POST", credentials: "include", body: fd });
    if (!res.ok) throw new Error(`HTTP ${String(res.status)}`);
    return (await res.json()) as { id: string; status: string; r2_key: string };
  },
};

// ---------- Display types (shared across pages) ----------
export interface Currency {
  code: string;
  name: string;
  symbol: string;
  is_active: boolean;
}

export interface Account {
  id: string;
  kind: string;
  name: string;
  institution: string | null;
  currency: string;
  current_balance_minor: number;
  status: string;
  is_liquid: boolean;
  is_retirement: boolean;
  last_synced_at: string | null;
}

export interface RetirementProjections {
  as_of: string;
  target_year: number | null;
  current_balance_eur_minor: number;
  current_monthly_contribution_eur_minor: number;
  targets: {
    target_id: string;
    label: string;
    currency: string;
    fire_amount_minor: number;
    monthly_income_at_retirement_minor: number;
    years_to_target: number | null;
    required_monthly_to_hit_target_year_minor: number | null;
  }[];
}

export interface Holding {
  account_id: string;
  account_name: string;
  provider: string | null;
  security_id: string | null;
  symbol: string;
  name: string | null;
  isin: string | null;
  exchange: string | null;
  asset_class: string;
  currency: string;
  quantity: string;
  mark_price_minor: number | null;
  mark_value_minor: number | null;
  cost_basis_minor: number | null;
  unrealized_pl_minor: number | null;
  unrealized_pl_eur_minor: number | null;
  value_eur_minor: number;
  weight_pct: number;
  observed_at: string;
}

export interface HoldingsResponse {
  as_of: string;
  totals: { market_value_eur_minor: number; unrealized_pl_eur_minor: number; count: number };
  items: Holding[];
}

export interface BuyingPowerRow {
  account_id: string;
  account_name: string;
  provider: string | null;
  currency: string;
  amount_minor: number;
  eur_equivalent_minor: number | null;
  observed_at: string;
}

export interface Lot {
  account_id: string;
  account_name: string;
  provider: string | null;
  security_id: string | null;
  symbol: string;
  name: string | null;
  asset_class: string;
  currency: string;
  quantity: string;
  mark_price_minor: number | null;
  mark_value_minor: number | null;
  cost_basis_minor: number | null;
  unrealized_pl_minor: number | null;
  unrealized_pl_eur_minor: number | null;
  value_eur_minor: number;
  opened_at: string | null;
  observed_at: string;
}

export interface Transaction {
  id: string;
  account_id: string;
  category_id: string | null;
  direction: "debit" | "credit";
  amount_minor: number;
  currency: string;
  description: string | null;
  counterparty: string | null;
  occurred_at: string;
}

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
}

export interface Security {
  id: string;
  isin: string | null;
  conid: string | null;
  symbol: string;
  asset_class: string;
  currency: string;
  exchange: string | null;
  name: string | null;
}

export interface SecurityPrice {
  id: string;
  security_id: string;
  price_date: string;
  currency: string;
  open_minor: number | null;
  high_minor: number | null;
  low_minor: number | null;
  close_minor: number;
  volume: number | null;
  source: string;
  observed_at: string;
}

// Per-security performance snapshot. Numeric (percent/quantity) columns arrive
// from Postgres as strings; minor-unit (bigint) columns as numbers.
export interface SecurityPerformanceRow {
  security_id: string;
  account_id: string | null;
  as_of: string;
  currency: string;
  quantity: string;
  avg_cost_minor: number | null;
  cost_basis_minor: number | null;
  mark_price_minor: number | null;
  market_value_minor: number | null;
  unrealized_pl_minor: number | null;
  realized_pl_minor: number | null;
  dividends_gross_minor: number | null;
  dividends_net_minor: number | null;
  fees_minor: number | null;
  total_benefit_minor: number | null;
  total_return_pct: string | null;
  yield_on_cost_pct: string | null;
  xirr_pct: string | null;
  local_return_pct: string | null;
  eur_return_pct: string | null;
  fx_effect_minor: number | null;
  eur_cost_basis_minor: number | null;
  eur_market_value_minor: number | null;
  eur_total_benefit_minor: number | null;
  first_buy_date: string | null;
  holding_days: number | null;
}

// The Performance list row = the consolidated snapshot joined to the instrument.
export interface StockPerformanceRow {
  security_id: string;
  symbol: string;
  name: string | null;
  asset_class: string;
  currency: string;
  as_of: string;
  quantity: string;
  market_value_minor: number | null;
  cost_basis_minor: number | null;
  unrealized_pl_minor: number | null;
  realized_pl_minor: number | null;
  dividends_gross_minor: number | null;
  dividends_net_minor: number | null;
  fees_minor: number | null;
  total_benefit_minor: number | null;
  total_return_pct: string | null;
  yield_on_cost_pct: string | null;
  xirr_pct: string | null;
  local_return_pct: string | null;
  eur_return_pct: string | null;
  fx_effect_minor: number | null;
  holding_days: number | null;
}

export interface SecurityNews {
  id: string;
  source: string;
  url: string;
  title: string;
  summary: string | null;
  sentiment: string | null;
  published_at: string | null;
  observed_at: string;
}

export interface StockNewsItem {
  id: string;
  source: string;
  url: string;
  title: string;
  summary: string | null;
  sentiment: string | null;
  published_at: string | null;
  relevance: string | null;
}

export interface SecurityLot {
  id: string;
  account_id: string;
  security_id: string;
  open_date: string;
  currency: string;
  quantity_open: string;
  quantity_remaining: string;
  cost_basis_minor: number;
  cost_per_share_minor: number | null;
  eur_cost_basis_minor: number | null;
  status: string;
  close_date: string | null;
  realized_pl_minor: number;
}

export interface MonthFlow {
  month: string;
  spend_eur_minor: number;
  income_eur_minor: number;
  net_eur_minor: number;
}

export interface CashFlow {
  as_of: string;
  current_month: MonthFlow;
  last_months: MonthFlow[];
  trailing_12_avg: { spend_eur_minor: number; income_eur_minor: number; net_eur_minor: number };
  by_category: {
    category_id: string | null;
    category_name: string;
    spend_eur_minor: number;
    count: number;
  }[];
  has_spend_data: boolean;
}

export interface Category {
  id: string;
  type: "expense" | "income";
  name: string;
  slug: string;
  is_archived: boolean;
}

export interface Budget {
  id: string;
  category_id: string | null;
  period: "daily" | "weekly" | "monthly" | "yearly";
  amount_minor: number;
  currency: string;
}

export interface RetirementTarget {
  id: string;
  label: string;
  fire_amount_minor: number;
  currency: string;
  expected_monthly_spend_minor: number | null;
  safe_withdrawal_rate: string;
}

export interface ExpensePlanItem {
  id: string;
  category: string;
  label: string;
  monthly_amount_minor: number;
  currency: string;
  sort_order: number;
}

export interface Webhook {
  id: string;
  name: string;
  target_url: string;
  entity: string;
  events: string[];
  is_active: boolean;
}

export interface SettingPublic {
  code: string;
  title: string;
  description: string;
  tags: string[];
  type: "string" | "number" | "boolean" | "encrypted_string" | "account_ids";
  enabled: boolean;
  has_override: boolean;
  has_value: boolean;
  /** Plain values only; null for encrypted_* (UI sees `has_value` instead). */
  value: string | number | boolean | string[] | null;
  group?: string;
  custom_renderer?: string;
  accept_account_kinds?: string[];
}

// Cash-leg reconciliation a sync computed when it re-anchored a balance.
// Mirrors the backend `AccountBalanceData["reconciliation"]` shape.
export interface LogReconciliation {
  prev_anchor_id: string;
  expected_cash_minor: number;
  actual_cash_minor: number;
  discrepancy_minor: number;
  txn_count: number;
  window_start: string;
  window_end: string;
  flagged: boolean;
}

export interface IntegrationLogEntry {
  id: string;
  integration_id: string;
  kind: "test" | "sync";
  triggered_by: string;
  actor: string | null;
  status: "running" | "ok" | "error";
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  updated: number | null;
  note: string | null;
  error: string | null;
  data: { reconciliation?: LogReconciliation } & Record<string, unknown>;
}

// Cross-integration log row — adds the joined integration identity so the
// central Logs table can show a human name without a second fetch.
export interface IntegrationLogRow extends IntegrationLogEntry {
  integration_code: string;
  integration_title: string;
  integration_label: string | null;
}

export interface IntegrationPublic {
  id: string;
  code: string;
  label: string | null;
  title: string;
  description: string;
  enabled: boolean;
  data: Record<string, unknown>;
  has_secrets: boolean;
  last_synced_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
}

export interface NetWorth {
  as_of: string;
  total_eur_minor: number;
  by_currency: { currency: string; total_minor: number; eur_equivalent_minor: number }[];
  by_account: { account_id: string; name: string; kind: string; currency: string; balance_minor: number; eur_equivalent_minor: number }[];
}

export interface FireScore {
  target_id: string;
  label: string;
  fire_amount_minor: number;
  currency: string;
  current_eur_minor: number;
  current_target_currency_minor: number;
  score: number;
  gap_eur_minor: number;
  runway_months: number | null;
}

export interface SpendingSummary {
  period: string;
  from: string;
  to: string;
  total_spend_eur_minor: number;
  total_income_eur_minor: number;
  net_eur_minor: number;
  by_category: { category_id: string | null; category_name: string; type: "expense" | "income"; total_eur_minor: number; count: number }[];
}
